import { useState } from 'react';
import styled from 'styled-components';
import { BaseCodeEditor } from '../../CodeEditor/CodeEditor';
import { Flex } from '../../Flex';
import CopyButton from '../components/CopyButton';
import { fontFamily, fontSize, radius, space } from '../../../style/utils';

const COLLAPSE_THRESHOLD = 12;

type Props = {
  value: string;
  language?: string;
};

const Container = styled.div`
  border-radius: ${radius('lg')};
  overflow: hidden;
  background: ${({ theme }) => theme.palette.surface.inverse};
`;

const Header = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: ${space('xxs', 'smMinus', 'xxs', 'md')};
  background: ${({ theme }) => theme.palette.surface.inverse};
`;

const Meta = styled(Flex)`
  align-items: center;
  gap: ${space('sm')};
  font-size: ${fontSize('sm')};
  color: ${({ theme }) => theme.palette.content.tertiary};
`;

const LanguageLabel = styled.span`
  font-family: ${fontFamily('mono')};
  text-transform: lowercase;
`;

const EditorScroll = styled.div`
  overflow-x: auto;
`;

const Footer = styled.button`
  display: block;
  width: 100%;
  padding: ${space('smMinus', 'md')};
  border: none;
  background: ${({ theme }) => theme.palette.surface.inverse};
  color: ${({ theme }) => theme.palette.accent.link};
  font-size: ${fontSize('sm')};
  text-align: left;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const ChatCodeBlock = ({ value, language }: Props) => {
  const [expanded, setExpanded] = useState(false);

  const lines = value.split('\n');
  const lineCount = lines.length;
  const isCollapsible = lineCount > COLLAPSE_THRESHOLD;
  const hiddenLineCount = lineCount - COLLAPSE_THRESHOLD;

  const displayValue =
    isCollapsible && !expanded
      ? lines.slice(0, COLLAPSE_THRESHOLD).join('\n')
      : value;

  return (
    <Container>
      <Header>
        <Meta>
          {language && <LanguageLabel>{language}</LanguageLabel>}
          <span>
            {lineCount} {lineCount === 1 ? 'line' : 'lines'}
          </span>
        </Meta>
        <CopyButton content={value} strokeColor="icon.inverse" />
      </Header>

      <EditorScroll>
        <BaseCodeEditor
          mode={language || 'text'}
          value={displayValue}
          fontSize={13}
          minLines={1}
          maxLines={Infinity}
          readOnly
          showPrintMargin={false}
          wrapEnabled={false}
        />
      </EditorScroll>

      {isCollapsible && (
        <Footer onClick={() => setExpanded((prev) => !prev)}>
          {expanded
            ? 'Show less'
            : `Show ${hiddenLineCount} more ${
                hiddenLineCount === 1 ? 'line' : 'lines'
              }`}
        </Footer>
      )}
    </Container>
  );
};

export default ChatCodeBlock;
